import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getChatHistory } from '@/api/agent'
import { fetchPostPreview } from '@/api/previews'
import { PlatformMockup } from '@/components/preview/PlatformMockup'
import { PanelHeader } from '@/components/ui/panel-header'
import { latestPreviewToken } from '@/lib/previewToken'
import { getPlatformBrand } from '@/lib/platforms'
import { useContentStore } from '@/stores/useContentStore'
import { useUiStore } from '@/stores/useUiStore'
import { JobsView } from './JobsView'

const SCHEDULED_LIMIT = 5

function PreviewSection({ token }: { token: string }) {
  const { data: preview } = useQuery({
    queryKey: ['post-preview', token],
    queryFn: () => fetchPostPreview(token),
    retry: false,
  })
  const variant = preview?.variants[0]
  const surface = variant?.surfaces[0]
  if (!preview || !variant || !surface) return null

  return (
    <section aria-labelledby="work-preview" className="flex flex-col items-center gap-2.5">
      <h2 id="work-preview" className="sr-only">
        Exact preview
      </h2>
      <div className="w-full max-w-[300px]">
        <PlatformMockup
          spec={preview.surfaces[surface.surface]}
          surface={surface}
          media={variant.media}
          businessName={preview.business.name}
        />
      </div>
      <p className="text-muted-foreground text-[0.8125rem]">
        Exact preview · {preview.surfaces[surface.surface]?.label ?? surface.surface}
      </p>
    </section>
  )
}

function ScheduledSection() {
  const ideas = useContentStore((state) => state.ideas)
  const [now] = useState(() => Date.now())
  const fetchIdeas = useContentStore((state) => state.fetchIdeas)

  useEffect(() => {
    void fetchIdeas()
  }, [fetchIdeas])

  const upcoming = ideas
    .filter((idea) => idea.scheduledAt && new Date(idea.scheduledAt).getTime() >= now)
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime())
    .slice(0, SCHEDULED_LIMIT)

  if (upcoming.length === 0) return null
  return (
    <section aria-labelledby="work-scheduled" className="flex flex-col gap-1">
      <h2 id="work-scheduled" className="text-muted-foreground pb-1.5 text-[0.8125rem] font-normal">
        Scheduled posts
      </h2>
      {upcoming.map((idea) => (
        <div key={idea.id} className="flex flex-col gap-0.5 py-2" data-testid="work-scheduled-post">
          <span className="truncate text-[0.9375rem]">{idea.title}</span>
          <span className="text-muted-foreground text-[0.8125rem]">
            {getPlatformBrand(idea.platform).label} ·{' '}
            {new Date(idea.scheduledAt!).toLocaleString([], {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
      ))}
    </section>
  )
}

/** What is at work: running jobs, the exact preview of the current work and what is
 * scheduled. Sections with no data are not rendered. The docked panel and the Work button's
 * popover both show this. */
export function WorkContent() {
  const { data: history } = useQuery({
    queryKey: ['agent-chat-history'],
    queryFn: () => getChatHistory(5),
    retry: false,
  })
  const token = latestPreviewToken(history?.messages ?? [])
  return (
    <div className="flex flex-col gap-6" data-testid="work-content">
      <JobsView />
      {token && <PreviewSection token={token} />}
      <ScheduledSection />
    </div>
  )
}

/** The chat page's collapsible right panel (wide screens only), toggled by the Work button. */
export function WorkPanel() {
  const collapsed = useUiStore((state) => state.workPanel.isCollapsed)
  const setDocked = useUiStore((state) => state.setWorkPanelDocked)

  useEffect(() => {
    setDocked(true)
    return () => setDocked(false)
  }, [setDocked])

  if (collapsed) return null
  return (
    <section
      aria-label="Work panel"
      data-testid="work-panel"
      className="border-border hidden w-[340px] shrink-0 flex-col overflow-y-auto border-l xl:flex"
    >
      {/* The header's Work button is the one show / hide control. */}
      <PanelHeader title="Work" />
      <div className="p-5">
        <WorkContent />
      </div>
    </section>
  )
}
